import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsIcon from '@mui/icons-material/Notifications';
import TuneIcon from '@mui/icons-material/Tune';
import { useNavigate } from 'react-router-dom';
import { notificationsApi } from '../../api/notifications';
import type { NotificationDto } from '../../api/types';
import { useNotifications } from '../../context/NotificationContext';

function relativeTime(iso: string): string {
  const ts = new Date(iso).getTime();
  const now = Date.now();
  const mins = Math.max(1, Math.floor((now - ts) / 60000));
  if (mins < 60) return `${mins} min fa`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} ore fa`;
  const days = Math.floor(hours / 24);
  return `${days} gg fa`;
}

const severityColor: Record<string, 'default' | 'info' | 'warning' | 'error'> = {
  INFO: 'info',
  WARNING: 'warning',
  CRITICAL: 'error',
};

export function NotificationBell() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { unreadCount, refresh } = useNotifications();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [items, setItems] = useState<NotificationDto[]>([]);

  const open = Boolean(anchorEl) || openDrawer;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await notificationsApi.list({ page: 0, size: 12 });
      setItems(data.content);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleBellClick = (el: HTMLElement) => {
    if (isMobile) {
      setOpenDrawer((prev) => !prev);
      return;
    }
    setAnchorEl((prev) => (prev ? null : el));
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setOpenDrawer(false);
  }, []);

  const closeAndNavigate = useCallback(
    (to: string) => {
      // Ensure focus is not retained inside a popover element that is being aria-hidden.
      const active = document.activeElement as HTMLElement | null;
      active?.blur();
      handleClose();
      requestAnimationFrame(() => navigate(to));
    },
    [handleClose, navigate],
  );

  const handleOpenNotification = async (n: NotificationDto) => {
    if (!n.isRead) {
      await notificationsApi.markRead(n.id);
      refresh();
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, isRead: true } : x)));
    }
    handleClose();
    if (n.targetUrl) navigate(n.targetUrl);
  };

  const handleMarkAll = async () => {
    setMarkingAll(true);
    try {
      await notificationsApi.markAllRead();
      setItems((prev) => prev.map((x) => ({ ...x, isRead: true })));
      refresh();
    } finally {
      setMarkingAll(false);
    }
  };

  const body = useMemo(
    () => (
      <Box
        sx={{
          /* xs: non occupare 100vw così resta fascia del backdrop cliccabile (Drawer temporaneo) */
          width: { xs: 'min(420px, calc(100vw - 40px))', sm: 420 },
          maxHeight: { xs: '100dvh', sm: 560 },
          p: 1.5,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap" mb={1}>
          <Typography variant="subtitle1" fontWeight={700}>
            Notifiche
          </Typography>
          <Stack direction="row" alignItems="center" gap={0.5} flexWrap="wrap" justifyContent="flex-end">
            <Button
              size="small"
              startIcon={<TuneIcon sx={{ fontSize: 18 }} />}
              onClick={() => closeAndNavigate('/app/settings/notifications')}
            >
              Impostazioni
            </Button>
            <Button size="small" onClick={() => closeAndNavigate('/app/notifications')}>
              Vedi tutte
            </Button>
            <IconButton size="small" onClick={handleClose} aria-label="Chiudi notifiche">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="caption" color="text.secondary">
            {unreadCount > 0 ? `${unreadCount} non lette` : 'Nessuna non letta'}
          </Typography>
          <Button size="small" onClick={handleMarkAll} disabled={markingAll || unreadCount === 0}>
            Segna tutte come lette
          </Button>
        </Stack>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress size={24} /></Box>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            Nessuna notifica.
          </Typography>
        ) : (
          <List disablePadding>
            {items.map((n) => (
              <ListItemButton
                key={n.id}
                onClick={() => handleOpenNotification(n)}
                sx={{
                  mb: 0.75,
                  borderRadius: 1.5,
                  minHeight: 48,
                  alignItems: 'flex-start',
                  bgcolor: n.isRead ? 'transparent' : 'action.hover',
                }}
              >
                <ListItemText
                  primaryTypographyProps={{ component: 'div' }}
                  secondaryTypographyProps={{ component: 'div' }}
                  primary={
                    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                      <Typography component="span" variant="body2" fontWeight={n.isRead ? 500 : 700}>
                        {n.title}
                      </Typography>
                      <Chip size="small" color={severityColor[n.severity] ?? 'default'} label={n.severity} />
                    </Stack>
                  }
                  secondary={
                    <Stack component="div" spacing={0.25}>
                      <Typography component="span" variant="body2" color="text.secondary" display="block">
                        {n.message}
                      </Typography>
                      <Typography component="span" variant="caption" color="text.disabled" display="block">
                        {relativeTime(n.createdAt)}
                      </Typography>
                    </Stack>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>
    ),
    [handleClose, items, loading, markingAll, navigate, refresh, unreadCount]
  );

  return (
    <>
      <IconButton color="inherit" size="small" onClick={(e) => handleBellClick(e.currentTarget)}>
        <Badge badgeContent={unreadCount > 0 ? unreadCount : undefined} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>
      {isMobile ? (
        <Drawer anchor="right" open={openDrawer} onClose={handleClose}>
          {body}
        </Drawer>
      ) : (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {body}
        </Popover>
      )}
    </>
  );
}
